import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CardIcon } from '../../assets/icons/credit-card.svg';
import { ReactComponent as DocumentIcon } from '../../assets/icons/document.svg';
import { ReactComponent as LightningIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import emspCdrApi from '../../utils/api/v2/emsp-cdr';
import TabToggle from '../tab-toggle/TabToggle';

interface EmspCdrSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * Database SVG icon component.
 * @returns {React.JSX.Element} The database icon.
 */
function DatabaseIcon() {
  return (
    <svg
      stroke="currentColor"
      fill="none"
      strokeWidth="2"
      viewBox="0 0 24 24"
      strokeLinecap="round"
      strokeLinejoin="round"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5V19A9 3 0 0 0 21 19V5" />
      <path d="M3 12A9 3 0 0 0 21 12" />
    </svg>
  );
}

/**
 * EmspCdrSidePanel component to display detail card information for an eMSP CDR.
 * @param {EmspCdrSidePanelProperties} properties - Component properties.
 * @returns {React.JSX.Element} The rendered side panel.
 */
function EmspCdrSidePanel(properties: EmspCdrSidePanelProperties) {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [cdrData, setCdrData] = useState<any>();
  const [activeTab, setActiveTab] = useState<'details' | 'cdr'>('details');

  /**
   * Helper function to get nested values from cdrData using dot notation and synonyms
   * @param {string} path - dot-separated key path
   * @param {boolean} date - flag to convert to date format
   * @param {boolean} [currency] - flag to format as currency
   * @param {boolean} [energy] - flag to format as energy
   * @returns {any} value or fallback string
   */
  const getNestedValue = (
    path: string,
    date: boolean,
    currency?: boolean,
    energy?: boolean,
  ): any => {
    let nestedValue: any = cdrData;
    try {
      const parts = path.split('.');
      parts.forEach((part) => {
        if (nestedValue !== null && nestedValue !== undefined) {
          const snakePart = part.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
          const possibleKeys = [part, snakePart];

          // Alias support for flexible parsing of multiple CDR standards
          if (part === 'startDateTime' || part === 'startTime') {
            possibleKeys.push('startDateTime', 'startTime', 'start_date_time', 'start_time');
          }
          if (part === 'endDateTime' || part === 'endTime') {
            possibleKeys.push('endDateTime', 'endTime', 'end_date_time', 'end_time');
          }
          if (part === 'authId' || part === 'authReference' || part === 'authReferenceId') {
            possibleKeys.push(
              'authId',
              'auth_id',
              'authReference',
              'auth_reference',
              'authRef',
              'auth_ref',
            );
          }
          if (
            part === 'lastBillingAttempt' ||
            part === 'lastBillingAttemptAt' ||
            part === 'lastPaymentAttempt' ||
            part === 'lastPaymentAttemptAt'
          ) {
            possibleKeys.push(
              'lastBillingAttempt',
              'last_billing_attempt',
              'lastBillingAttemptAt',
              'last_billing_attempt_at',
              'lastPaymentAttempt',
              'last_payment_attempt',
              'lastPaymentAttemptAt',
              'last_payment_attempt_at',
            );
          }
          if (part === 'billingStatus' || part === 'paymentStatus') {
            possibleKeys.push('billingStatus', 'billing_status', 'paymentStatus', 'payment_status');
          }
          if (part === 'isBilled' || part === 'isPaid') {
            possibleKeys.push('isBilled', 'is_billed', 'isPaid', 'is_paid');
          }
          if (
            part === 'billingReason' ||
            part === 'paymentFailedReason' ||
            part === 'paymentReason'
          ) {
            possibleKeys.push(
              'billingReason',
              'billing_reason',
              'paymentFailedReason',
              'payment_failed_reason',
              'paymentReason',
              'payment_reason',
            );
          }
          if (part === 'billingError' || part === 'paymentError') {
            possibleKeys.push('billingError', 'billing_error', 'paymentError', 'payment_error');
          }
          if (part === 'billingAttempts' || part === 'paymentAttempts') {
            possibleKeys.push('billingAttempts', 'billing_attempts', 'paymentAttempts', 'payment_attempts');
          }
          if (part === 'billedAt' || part === 'paidAt') {
            possibleKeys.push('billedAt', 'billed_at', 'paidAt', 'paid_at');
          }
          if (part === 'invoiceNumber' || part === 'invoiceId') {
            possibleKeys.push('invoiceNumber', 'invoice_number', 'invoiceId', 'invoice_id');
          }

          const resolvedKey = possibleKeys.find(
            (key) => nestedValue[key] !== undefined && nestedValue[key] !== null,
          );
          nestedValue = resolvedKey !== undefined ? nestedValue[resolvedKey] : undefined;
        }
      });

      // Root level fallbacks
      if (path === 'externalId' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue = cdrData?.externalId || cdrData?.external_id || cdrData?.cdrDetails?.id;
      }
      if (
        path === 'cdrDetails.userCustomId' &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.userCustomId ||
          cdrData?.cdrDetails?.userCustomId ||
          cdrData?.cdrDetails?.user_custom_id;
      }
      if (
        path === 'cdrDetails.totalEnergy' &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.totalEnergy ||
          cdrData?.total_energy ||
          cdrData?.cdrDetails?.totalEnergy ||
          cdrData?.cdrDetails?.total_energy;
      }
      if (path === 'cdrDetails.totalCost' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue =
          cdrData?.totalCost ||
          cdrData?.total_cost ||
          cdrData?.cdrDetails?.totalCost ||
          cdrData?.cdrDetails?.total_cost;
      }
      if (path === 'emspFees.finalAmount' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue =
          cdrData?.finalAmount ||
          cdrData?.final_amount ||
          cdrData?.emspFees?.finalAmount ||
          cdrData?.emspFees?.final_amount ||
          cdrData?.amount;
      }
      if (path === 'emspFees.cdrTotalCost' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue =
          cdrData?.cdrTotalCost ||
          cdrData?.cdr_total_cost ||
          cdrData?.emspFees?.cdrTotalCost ||
          cdrData?.emspFees?.cdr_total_cost;
      }
      if (
        path === 'emspFees.platformAmount' &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.platformAmount ||
          cdrData?.platform_amount ||
          cdrData?.emspFees?.platformAmount ||
          cdrData?.emspFees?.platform_amount;
      }
      if (
        path === 'emspFees.sessionAmount' &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.sessionAmount ||
          cdrData?.session_amount ||
          cdrData?.emspFees?.sessionAmount ||
          cdrData?.emspFees?.session_amount;
      }

      // Root level payment/billing fallbacks
      if (
        (path === 'payment.billingStatus' || path === 'payment.paymentStatus') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paymentStatus ||
          cdrData?.billingStatus ||
          cdrData?.payment?.billingStatus ||
          cdrData?.payment?.paymentStatus;
      }
      if (
        (path === 'payment.isBilled' || path === 'payment.isPaid') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        if (cdrData?.isPaid !== undefined) {
          nestedValue = cdrData.isPaid;
        } else if (cdrData?.isBilled !== undefined) {
          nestedValue = cdrData.isBilled;
        } else if (cdrData?.payment?.isPaid !== undefined) {
          nestedValue = cdrData.payment.isPaid;
        } else {
          nestedValue = cdrData?.payment?.isBilled;
        }
      }
      if (
        (path === 'payment.billingReason' || path === 'payment.paymentReason') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paymentFailedReason ||
          cdrData?.billingReason ||
          cdrData?.payment?.billingReason ||
          cdrData?.payment?.paymentFailedReason;
      }
      if (
        (path === 'payment.billingError' || path === 'payment.paymentError') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paymentError ||
          cdrData?.billingError ||
          cdrData?.payment?.billingError ||
          cdrData?.payment?.paymentError;
      }
      if (
        (path === 'payment.billingAttempts' || path === 'payment.paymentAttempts') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paymentAttempts ||
          cdrData?.billingAttempts ||
          cdrData?.payment?.billingAttempts ||
          cdrData?.payment?.paymentAttempts;
      }
      if (path === 'payment.paymentMethod' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue = cdrData?.paymentMethod || cdrData?.payment?.paymentMethod;
      }
      if (path === 'payment.paymentIntentId' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue = cdrData?.paymentIntentId || cdrData?.payment?.paymentIntentId;
      }
      if (path === 'payment.gatewayTransactionId' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue = cdrData?.gatewayTransactionId || cdrData?.payment?.gatewayTransactionId;
      }
      if (
        (path === 'payment.lastBillingAttemptAt' || path === 'payment.lastPaymentAttemptAt') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.lastPaymentAttemptAt ||
          cdrData?.lastBillingAttemptAt ||
          cdrData?.payment?.lastBillingAttemptAt ||
          cdrData?.payment?.lastPaymentAttemptAt;
      }
      if (
        (path === 'payment.billedAt' || path === 'payment.paidAt') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paidAt ||
          cdrData?.billedAt ||
          cdrData?.payment?.billedAt ||
          cdrData?.payment?.paidAt;
      }

      if (nestedValue === null || nestedValue === undefined) {
        return '—';
      }

      if (date) {
        return `${ConvertUTCtoDate(nestedValue)} - ${FormatUtcString(ConvertUTCtoHHMM(nestedValue))}`;
      }

      if (energy) {
        const energyValue = Number(nestedValue);
        if (!Number.isNaN(energyValue)) {
          // If totalEnergy is already formatted in kWh (like 18.3), don't divide by 1000.
          // Standard OCPI specifies Wh, so values >= 1000 are divided by 1000.
          if (energyValue >= 1000) {
            return `${ToFixedValue(energyValue / 1000, 3)} kWh`;
          }
          return `${ToFixedValue(energyValue, 3)} kWh`;
        }
      }

      if (currency) {
        const numberValue = Number(nestedValue);
        if (!Number.isNaN(numberValue)) {
          const currencyString = cdrData?.cdrDetails?.currency || cdrData?.currency || 'SAR';
          return `${ToFixedValue(numberValue)} ${currencyString}`;
        }
      }
    } catch (error) {
      console.error(error);
    }
    return nestedValue !== undefined && nestedValue !== null ? String(nestedValue) : '—';
  };

  /**
   * Helper function to render header icons for each detail section.
   * @param {string} key - The icon key to match.
   * @returns {React.JSX.Element | null} The matching icon element or null.
   */
  const getSectionIcon = (key: string): React.JSX.Element | null => {
    switch (key) {
      case 'cdr-information':
        return <DocumentIcon />;
      case 'location':
        return <LocationIcon />;
      case 'energy-cost':
        return <LightningIcon />;
      case 'emsp-fees':
        return <CardIcon />;
      case 'payment':
        return <CardIcon />;
      case 'metadata':
        return <DatabaseIcon />;
      default:
        return null;
    }
  };

  const cdrMap = [
    {
      header: t('cdrInformation', { defaultValue: 'CDR Information' }),
      iconKey: 'cdr-information',
      keyValuePair: [
        { label: t('externalId', { defaultValue: 'External ID' }), key: 'externalId' },
        { label: t('sessionId', { defaultValue: 'Session ID' }), key: 'cdrDetails.sessionId' },
        {
          label: t('countryCode', { defaultValue: 'Country Code' }),
          key: 'cdrDetails.countryCode',
        },
        { label: t('partyId', { defaultValue: 'Party ID' }), key: 'cdrDetails.partyId' },
        {
          label: t('userCustomId', { defaultValue: 'User Custom ID' }),
          key: 'cdrDetails.userCustomId',
        },
        {
          label: t('startTime', { defaultValue: 'Start Time' }),
          key: 'cdrDetails.startTime',
          date: true,
        },
        {
          label: t('endTime', { defaultValue: 'End Time' }),
          key: 'cdrDetails.endTime',
          date: true,
        },
        {
          label: t('authenticationMethod', { defaultValue: 'Authentication Method' }),
          key: 'cdrDetails.authMethod',
        },
        {
          label: t('authenticationReference', { defaultValue: 'Authentication Reference' }),
          key: 'cdrDetails.authReference',
        },
        { label: t('currency', { defaultValue: 'Currency' }), key: 'cdrDetails.currency' },
        { label: t('source', { defaultValue: 'Source' }), key: 'cdrDetails.source' },
        {
          label: t('lastUpdated', { defaultValue: 'Last Updated' }),
          key: 'cdrDetails.lastUpdated',
          date: true,
        },
      ],
    },
    {
      header: t('location', { defaultValue: 'Location' }),
      iconKey: 'location',
      keyValuePair: [
        { label: t('locationId', { defaultValue: 'Location ID' }), key: 'cdrDetails.location.id' },
      ],
    },
    {
      header: t('energyCost', { defaultValue: 'Energy & Cost' }),
      iconKey: 'energy-cost',
      keyValuePair: [
        {
          label: t('totalEnergy', { defaultValue: 'Total Energy' }),
          key: 'cdrDetails.totalEnergy',
          energy: true,
        },
        {
          label: t('totalTime', { defaultValue: 'Total Time' }),
          key: 'cdrDetails.totalTime',
          /**
           * Render total time custom details view
           * @param {any} value - The input value object
           * @returns {React.JSX.Element} The total time element
           */
          view: (value: any) => {
            let displayTime = '—';
            const totalTime = value?.cdrDetails?.totalTime || value?.totalTime;
            if (
              totalTime !== undefined &&
              totalTime !== null &&
              !Number.isNaN(Number(totalTime))
            ) {
              const totalSeconds = Math.round(Number(totalTime) * 3600);
              if (totalSeconds <= 0) {
                displayTime = '0s';
              } else {
                const days = Math.floor(totalSeconds / 86400);
                const hours = Math.floor((totalSeconds % 86400) / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;

                if (days > 0) {
                  displayTime = `${days}d ${hours}h ${minutes}m ${seconds}s`;
                } else if (hours > 0) {
                  displayTime = `${hours}h ${minutes}m ${seconds}s`;
                } else if (minutes > 0) {
                  displayTime = `${minutes}m ${seconds}s`;
                } else {
                  displayTime = `${seconds}s`;
                }
              }
            }
            return (
              <div className="transaction-item">
                <h2>{t('totalTime', { defaultValue: 'Total Time' })}: </h2>
                <p>{displayTime}</p>
              </div>
            );
          },
        },
        {
          label: t('totalCost', { defaultValue: 'Total Cost' }),
          key: 'cdrDetails.totalCost',
          currency: true,
        },
        {
          label: t('tariffs', { defaultValue: 'Tariffs' }),
          key: 'cdrDetails.tariffs',
          /**
           * Render tariffs custom details view
           * @param {any} value - The input value object
           * @returns {React.JSX.Element} The tariffs list element
           */
          view: (value: any) => {
            const list = value?.cdrDetails?.tariffs || value?.tariffs;
            if (!Array.isArray(list) || list.length === 0) {
              return (
                <div className="transaction-item">
                  <h2>{t('tariffs', { defaultValue: 'Tariffs' })}: </h2>
                  <p>{t('noDataAvailable', { defaultValue: 'No Data Available' })}</p>
                </div>
              );
            }
            return (
              <div className="transaction-item">
                <h2>{t('tariffs', { defaultValue: 'Tariffs' })}: </h2>
                <div>
                  {list.map((item: any, index: number) => {
                    const tariffText =
                      typeof item === 'string' ? item : item?.id || item?.tariffId || '—';
                    return (
                      <div key={index}>
                        <p>{tariffText}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          },
        },
        {
          label: t('chargingPeriods', { defaultValue: 'Charging Periods' }),
          key: 'cdrDetails.chargingPeriods',
          /**
           * Render charging periods custom details view
           * @param {any} value - The input value object
           * @returns {React.JSX.Element} The charging periods element
           */
          view: (value: any) => {
            const list = value?.cdrDetails?.chargingPeriods || value?.chargingPeriods;
            if (!Array.isArray(list) || list.length === 0) {
              return (
                <div className="transaction-item">
                  <h2>{t('chargingPeriods', { defaultValue: 'Charging Periods' })}: </h2>
                  <p>{t('noDataAvailable', { defaultValue: 'No Data Available' })}</p>
                </div>
              );
            }
            return (
              <div className="transaction-item">
                <h2>{t('chargingPeriods', { defaultValue: 'Charging Periods' })}: </h2>
                <div>
                  {list.map((item: any, index: number) => (
                    <div key={index}>
                      <p>
                        {t('period', { defaultValue: 'Period' })} {index + 1}:{' '}
                        {item.dimensions
                          ?.map((dim: any) => `${dim.type}: ${dim.volume}`)
                          .join(', ') || '—'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          },
        },
      ],
    },
    {
      header: t('mobilityFees', { defaultValue: 'Mobility Fees' }),
      iconKey: 'emsp-fees',
      keyValuePair: [
        {
          label: t('platformFee', { defaultValue: 'Platform Fee' }),
          key: 'emspFees.platformFee',
          /**
           * Render platform fee custom details view based on fee type
           * @param {any} value - The input value object
           * @returns {React.JSX.Element} The platform fee element
           */
          view: (value: any) => {
            const fee =
              value?.emspFees?.platformFee !== undefined
                ? value?.emspFees?.platformFee
                : value?.platformFee;
            const feeType =
              value?.emspFees?.platformFeeType !== undefined
                ? value?.emspFees?.platformFeeType
                : value?.platformFeeType;
            if (fee === undefined || fee === null || fee === '—') {
              return (
                <div className="grid-item">
                  <span className="item-label">
                    {t('platformFee', { defaultValue: 'Platform Fee' })}
                  </span>
                  <span className="item-value">—</span>
                </div>
              );
            }
            let formattedValue = '';
            if (String(feeType).toLowerCase() === 'percentage') {
              formattedValue = `${fee} %`;
            } else {
              const currencyString = value?.cdrDetails?.currency || value?.currency || 'SAR';
              formattedValue = `${ToFixedValue(Number(fee))} ${currencyString}`;
            }
            return (
              <div className="grid-item">
                <span className="item-label">
                  {t('platformFee', { defaultValue: 'Platform Fee' })}
                </span>
                <span className="item-value">{formattedValue}</span>
              </div>
            );
          },
        },
        {
          label: t('platformAmount', { defaultValue: 'Platform Amount' }),
          key: 'emspFees.platformAmount',
          currency: true,
        },

        {
          label: t('sessionFee', { defaultValue: 'Session Fee' }),
          key: 'emspFees.sessionFee',
          /**
           * Render session fee custom details view based on fee type
           * @param {any} value - The input value object
           * @returns {React.JSX.Element} The session fee element
           */
          view: (value: any) => {
            const fee =
              value?.emspFees?.sessionFee !== undefined
                ? value?.emspFees?.sessionFee
                : value?.sessionFee;
            const feeType =
              value?.emspFees?.sessionFeeType !== undefined
                ? value?.emspFees?.sessionFeeType
                : value?.sessionFeeType;
            if (fee === undefined || fee === null || fee === '—') {
              return (
                <div className="grid-item">
                  <span className="item-label">
                    {t('sessionFee', { defaultValue: 'Session Fee' })}
                  </span>
                  <span className="item-value">—</span>
                </div>
              );
            }
            let formattedValue = '';
            if (String(feeType).toLowerCase() === 'percentage') {
              formattedValue = `${fee} %`;
            } else {
              const currencyString = value?.cdrDetails?.currency || value?.currency || 'SAR';
              formattedValue = `${ToFixedValue(Number(fee))} ${currencyString}`;
            }
            return (
              <div className="grid-item">
                <span className="item-label">
                  {t('sessionFee', { defaultValue: 'Session Fee' })}
                </span>
                <span className="item-value">{formattedValue}</span>
              </div>
            );
          },
        },
        {
          label: t('sessionAmount', { defaultValue: 'Session Amount' }),
          key: 'emspFees.sessionAmount',
          currency: true,
        },

        {
          label: t('cdrTotalCost', { defaultValue: 'CDR Total Cost' }),
          key: 'emspFees.cdrTotalCost',
          currency: true,
        },
        {
          label: t('finalAmount', { defaultValue: 'Final Amount' }),
          key: 'emspFees.finalAmount',
          currency: true,
        },
      ],
    },
    {
      header: t('payment', { defaultValue: 'Payment' }),
      iconKey: 'payment',
      keyValuePair: [
        {
          label: t('paymentStatus', { defaultValue: 'Payment Status' }),
          key: 'payment.paymentStatus',
        },
        {
          label: t('isPaid', { defaultValue: 'Is Paid' }),
          key: 'payment.isPaid',
          /**
           * Render isBilled custom details view
           * @param {any} value - The input value object
           * @returns {React.JSX.Element} The isBilled element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('isPaid', { defaultValue: 'Is Paid' })}: </h2>
              <p>
                {value?.payment?.isBilled || value?.payment?.isPaid || value?.isPaid || value?.isBilled
                  ? t('yes', { defaultValue: 'Yes' })
                  : t('no', { defaultValue: 'No' })}
              </p>
            </div>
          ),
        },
        {
          label: t('paymentError', { defaultValue: 'Payment Error' }),
          key: 'payment.paymentError',
        },
        {
          label: t('paymentAttempts', { defaultValue: 'Payment Attempts' }),
          key: 'payment.paymentAttempts',
        },
        {
          label: t('paymentMethod', { defaultValue: 'Payment Method' }),
          key: 'payment.paymentMethod',
        },
        {
          label: t('paymentIntentId', { defaultValue: 'Payment Intent ID' }),
          key: 'payment.paymentIntentId',
        },
        {
          label: t('gatewayTransactionId', { defaultValue: 'Gateway Transaction ID' }),
          key: 'payment.gatewayTransactionId',
        },
        {
          label: t('lastPaymentAttempt', { defaultValue: 'Last Payment Attempt' }),
          key: 'payment.lastPaymentAttemptAt',
          date: true,
        },
        {
          label: t('paidAt', { defaultValue: 'Paid At' }),
          key: 'payment.paidAt',
          date: true,
        },
      ],
    },
    {
      header: t('metadata', { defaultValue: 'Metadata' }),
      iconKey: 'metadata',
      keyValuePair: [
        { label: t('objectId', { defaultValue: 'Object ID' }), key: 'id' },
        { label: t('createdAt', { defaultValue: 'Created At' }), key: 'createdAt', date: true },
        { label: t('updatedAt', { defaultValue: 'Updated At' }), key: 'updatedAt', date: true },
      ],
    },
  ];

  /**
   * Function to fetch CDR details
   * @param {string} cdrId - The CDR ID
   * @returns {Promise<void>} Resolves when fetch completes
   */
  const handleFetchCdrDetails = async (cdrId: string) => {
    emspCdrApi
      .fetchEmspCdrById(cdrId)
      .then((response) => {
        setCdrData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message:
              error?.response?.data?.message ||
              t('errorMessage', { defaultValue: 'Failed to fetch CDR details.' }),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  useEffect(() => {
    let cdrObjectId;
    if (fetchIdFrom === 'queryParams') {
      cdrObjectId =
        searchParameters.get('cdrObjectId') ||
        searchParameters.get('cdrId') ||
        searchParameters.get('id');
    } else if (fetchIdFrom === 'props') {
      cdrObjectId = id;
    }

    if (!cdrObjectId) {
      return;
    }

    handleFetchCdrDetails(cdrObjectId);
  }, [searchParameters, id]);

  const activeSections = cdrMap.filter((section) => {
    if (activeTab === 'details') {
      return (
        section.iconKey === 'emsp-fees' ||
        section.iconKey === 'payment' ||
        section.iconKey === 'metadata'
      );
    }
    return (
      section.iconKey === 'energy-cost' ||
      section.iconKey === 'cdr-information' ||
      section.iconKey === 'location'
    );
  });

  return (
    <div className="emsp-cdr-side-panel-wrap">
      {/* Scrollable Middle Content */}
      <div className="panel-scrollable-content">
        <div className="summary-highlight-card">
          <div className="summary-highlight-header">
            <span className="summary-title">
              {t('finalAmount', { defaultValue: 'Final Amount' })}
            </span>
            <span className="summary-value">
              {getNestedValue('emspFees.finalAmount', false, true)}
            </span>
          </div>
          <div className="summary-highlight-grid">
            <div className="grid-cell">
              <span className="cell-label">
                {t('totalEnergy', { defaultValue: 'Total Energy' })}
              </span>
              <span className="cell-value">
                {getNestedValue('cdrDetails.totalEnergy', false, false, true)}
              </span>
            </div>
            <div className="grid-cell">
              <span className="cell-label">
                {t('cdrTotalCost', { defaultValue: 'CDR Total Cost' })}
              </span>
              <span className="cell-value">
                {getNestedValue('emspFees.cdrTotalCost', false, true)}
              </span>
            </div>
            <div className="grid-cell">
              <span className="cell-label">
                {t('platformAmount', { defaultValue: 'Platform Amount' })}
              </span>
              <span className="cell-value">
                {getNestedValue('emspFees.platformAmount', false, true)}
              </span>
            </div>
            <div className="grid-cell">
              <span className="cell-label">
                {t('sessionAmount', { defaultValue: 'Session Amount' })}
              </span>
              <span className="cell-value">
                {getNestedValue('emspFees.sessionAmount', false, true)}
              </span>
            </div>
          </div>
        </div>
        {/* Tab Navigation */}
        <TabToggle
          tabs={[
            {
              label: t('detailsTransaction', { defaultValue: 'Details Transaction' }),
              value: 'details',
            },
            {
              label: t('cdrForNetwork', { defaultValue: 'CDR For Network' }),
              value: 'cdr',
            },
          ]}
          defaultTab="details"
          isLabelValue
          onChange={(value) => setActiveTab(value as 'details' | 'cdr')}
        />

        {/* Redesigned Sections */}
        {activeSections.map((section, index) => (
          <div className="detail-section-card" key={index}>
            <div className={`card-header ${section.iconKey}`}>
              {getSectionIcon(section.iconKey)}
              <h3>{section.header}</h3>
            </div>
            <div className="card-body-grid">
              {section.keyValuePair.map((item: any, itemIndex: number) => {
                if (item.view) {
                  return <React.Fragment key={itemIndex}>{item.view(cdrData)}</React.Fragment>;
                }
                const nestedValue = getNestedValue(
                  item.key,
                  item.date || false,
                  item.currency || false,
                  item.energy || false,
                );

                const isStatus =
                  (item.key === 'payment.billingStatus' || item.key === 'payment.paymentStatus') &&
                  nestedValue === 'SUCCESS';

                return (
                  <div className="grid-item" key={itemIndex}>
                    <span className="item-label">{item.label}</span>
                    <span className={`item-value ${isStatus ? 'status-success' : ''}`}>
                      {nestedValue}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EmspCdrSidePanel;
